import React from 'react'
import { graphql } from 'gatsby'
import styled from '@emotion/styled'

import type { Paragraph, ParagraphComponent } from '../types'
import type { MediaVideo, MediaVideoFile } from '../../Media'

import { classNames } from '../../ui/utils'
import { media } from '../../../styles/variables'
import useToggle from '../../../hooks/Toggle'
import Container from '../../Container'
import WatchMoreModal from './WatchMoreModal'
import PlayButton from './PlayButton'
import Media from '../../Media'
import { css } from '@emotion/react'

export interface HeroVideoParagraph extends Paragraph {
	field_text: {
		processed: string
	}
	relationships: {
		field_media: MediaVideoFile
		embed?: MediaVideo
	}
}

const HeroVideo: ParagraphComponent<HeroVideoParagraph> = ({ className, paragraph }) => {
	const [showModal, toggle] = useToggle()

	return (
		<>
			<div className={classNames('hero', className)}>
				<Media className="landscape" source={paragraph.relationships.field_media} />
				<Container>
					<div className="body">
						<div className="body-inner">
							<div dangerouslySetInnerHTML={{ __html: paragraph.field_text.processed }} />
							{paragraph.relationships.embed && (
								<div className="play-video">
									<a onClick={toggle}>
										<div className="play-icon">
											<PlayButton />
										</div>
									</a>
									<span>Katso video</span>
								</div>
							)}
						</div>
					</div>
				</Container>
			</div>

			{paragraph.relationships.embed && (
				<WatchMoreModal isOpen={showModal} toggle={toggle} embed={paragraph.relationships.embed} />
			)}
		</>
	)
}

const Typography = css`
	color: white;

	.body-inner {
		background-color: rgba(0, 0, 0, 0.5);
		padding: var(--spacing);
	}
	h1 {
		text-transform: uppercase;
		font-size: 3.5rem;
		${media.down('sm')} {
			font-size: 2.5rem;
		}
		${media.down('xs')} {
			font-size: 2rem;
		}
	}

	.play-video {
		display: flex;
		align-items: center;

		${media.down('sm')} {
			justify-content: center;
		}

		> * + * {
			margin-left: calc(var(--spacing) * 0.5);
		}

		a {
			color: white;
		}
	}

	.play-icon {
		position: relative;
		display: flex;
		cursor: pointer;

		&:hover:before {
			content: '';
			position: absolute;
			display: block;
			width: 300%;
			height: 300%;
			margin-left: -100%;
			margin-top: -100%;
			border-radius: 50%;
			background-color: #01a4e9;
			animation: pulse-ring 1.25s cubic-bezier(0.215, 0.61, 0.355, 1) infinite;
		}
	}

	svg {
		fill: white;
		width: calc(var(--spacing) * 2);
	}

	@keyframes pulse-ring {
		0% {
			transform: scale(0.33);
		}
		80%,
		100% {
			opacity: 0;
		}
	}
`

export default styled(HeroVideo)`
	min-height: calc(100vh - 15px);

	position: relative;
	overflow: hidden;
	display: flex;

	${Typography};

	.media {
		position: absolute;

		top: -50%;
		left: -50%;
		width: 200%;
		height: 200%;
	}

	.media:after {
		content: '';
		position: absolute;
		top: 25%;
		right: 25%;
		bottom: 25%;
		left: 25%;
		box-shadow: 0 0 100px 10px rgba(0, 0, 0, 1) inset;
	}

	/**
	 * Video centering:
	 * https://stackoverflow.com/questions/27850472/html5-video-background-keep-center-of-video-in-center
	 */
	video {
		position: absolute;
		top: 0;
		bottom: 0;
		right: 0;
		left: 0;
		margin: auto;
		min-height: 50%;
		min-width: 50%;
	}

	.body {
		flex: 0 33%;

		${media.up('md')} {
			justify-content: center;
			flex-direction: column;
			display: flex;
		}

		${media.down('sm')} {
			padding-top: 100px;
		}
	}

	${media.down('sm')} {
		.body {
			flex: 1;
			text-align: center;
		}
	}

	${Container} {
		flex: 1;
		display: flex;
		align-items: stretch;
		justify-content: flex-end;
	}
`

export const query = graphql`
	fragment HeroVideoParagraph on paragraph__hero_video {
		drupal_internal__id
		field_text {
			processed
		}
		relationships {
			field_media {
				__typename
				...MediaVideoFile
			}
			embed: field_media_alternative {
				__typename
				...MediaVideo
			}
		}
	}
`
